import React, { useState, useMemo, useCallback } from 'react';
import { Eye } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { Card, CardContent } from '../../../../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { CollapsibleFilterBar } from '../materials/tabs/Filters/CollapsibleFilterBar';
import type { AuditTrailItem } from './auditTrailTypes';
import { useAuditTrailEntityHistoryQuery } from './useAuditTrailEntityHistoryQuery';
import {
  formatAuditTrailDateTime,
  getActorDisplay,
  getAuditSummary,
  getEventDisplayLabel,
} from './auditTrailConstants';
import {
  AuditEmptyState,
  AuditErrorBanner,
  AuditLoadingState,
  SeverityBadge,
  severityBorderClass,
} from './auditTrailUi';

const ENTITY_TYPE_OPTIONS = [
  { value: 'REPORT', label: 'Report' },
  { value: 'PQR_REPORT', label: 'PQR report' },
  { value: 'PRODUCTION_WELD', label: 'Production weld' },
  { value: 'PIPE_DOCUMENT', label: 'Pipe document' },
  { value: 'PIPE_MATERIAL_REPORT', label: 'Pipe mill report' },
  { value: 'USER', label: 'User' },
  { value: 'REGISTRY', label: 'Registry item' },
  { value: '__any__', label: 'Any type' },
];

interface EntityHistoryViewProps {
  projectId?: number;
  projectName?: string | null;
  onOpenDetail: (row: AuditTrailItem) => void;
}

export const EntityHistoryView: React.FC<EntityHistoryViewProps> = ({
  projectId,
  projectName,
  onOpenDetail,
}) => {
  const [entityType, setEntityType] = useState('REPORT');
  const [entityId, setEntityId] = useState('');

  const { items, total, loading, error, hasQuery, isBrowseMode } = useAuditTrailEntityHistoryQuery(
    projectId,
    entityType,
    entityId,
    projectName
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (entityId.trim()) n += 1;
    if (entityType !== 'REPORT') n += 1;
    return n;
  }, [entityId, entityType]);

  const clearFilters = useCallback(() => {
    setEntityType('REPORT');
    setEntityId('');
  }, []);

  return (
    <div className="space-y-4">
      <CollapsibleFilterBar
        searchQuery={entityId}
        onSearchChange={setEntityId}
        searchPlaceholder="Report name, PQR number, weld number, file name…"
        activeFilterCount={activeFilterCount}
        filteredCount={hasQuery ? total : undefined}
        filteredCountLabel="history events"
        onResetFilters={clearFilters}
      >
        <div className="flex flex-col gap-1 min-w-[160px]">
          <Label className="text-[11px] text-heritage-grey">Entity type</Label>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="h-9 text-[13px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CollapsibleFilterBar>

      {error ? <AuditErrorBanner message={error} /> : null}

      <Card className="border-[#E2E5EA] shadow-none">
        <CardContent className="p-6">
          {loading && items.length === 0 ? (
            <AuditLoadingState message="Loading entity history…" />
          ) : !hasQuery ? (
            <AuditEmptyState
              title="Select an entity type"
              description="Choose a type above to browse recent history, or search by report name, PQR number, or weld ID."
            />
          ) : items.length === 0 ? (
            <AuditEmptyState
              title="No history found"
              description={
                isBrowseMode
                  ? 'No audit events for this entity type yet. Generate a report or try a different type.'
                  : 'Try a different search term or switch entity type (e.g. PQR report).'
              }
            />
          ) : (
            <>
              <p className="text-[13px] text-heritage-grey mb-4">
                {isBrowseMode ? 'Recent ' : ''}
                {total} event{total === 1 ? '' : 's'} · newest first
                {isBrowseMode ? ' · search above to narrow' : ''}
              </p>
              <div className="space-y-3">
                {items.map((row) => {
                  const actor = getActorDisplay(row);
                  return (
                    <div
                      key={row.id}
                      className={cn(
                        'rounded-lg border border-[#EEF0F3] border-l-[3px] bg-page-bg/50 p-4 hover:bg-page-bg cursor-pointer transition-colors',
                        severityBorderClass(row.severity)
                      )}
                      onClick={() => onOpenDetail(row)}
                      onKeyDown={(e) => e.key === 'Enter' && onOpenDetail(row)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-[11px] font-semibold text-heritage-grey">
                          {formatAuditTrailDateTime(row.created_at)}
                        </span>
                        <SeverityBadge severity={row.severity} />
                      </div>
                      <div className="text-[13px] font-medium text-text-grey">
                        {getAuditSummary(row)}
                      </div>
                      <div className="text-[11px] text-heritage-grey mt-1">
                        {getEventDisplayLabel(row)} · {actor.primary}
                        {row.entity_name || row.entity_id ? (
                          <span>
                            {' '}
                            · {row.entity_name || row.entity_id}
                          </span>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 mt-2 text-[12px] text-blueprint-blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenDetail(row);
                        }}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        View
                      </Button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
